import { AccordionDetails, AccordionSummary, Box, IconButton, SxProps, Theme } from '@mui/material';
import MuiAccordion, { AccordionProps as MuiAccordionProps } from '@mui/material/Accordion';
import { TransitionProps } from '@mui/material/transitions/transition';
import {
  HTMLAttributes,
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  SyntheticEvent,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Icon } from '@skodaflow/web-library';
import { styled } from '@mui/material';

/**
 * @interface AccordionItem
 * @description The interface describing the object that has to be provided to the array property 'items' in Accordion component.
 * When accordion.allowMultipleExpand is set to false and more than one item has isDefaultExpanded set to true, only the first occurrence in the array will be deafault expanded.
 * If no id is provided the component item receives indexed id -> panel${index}. The object is not changes.
 * The property id is also the key of the every accordion component.
 */
export interface AccordionItem {
  id?: string;
  title: string;
  titleValue?: string | undefined;
  content: string | ReactNode;
  isDefaultExpanded?: boolean;
}

/**
 * @interface AccordionProps
 * @description Base definition of SkodaFlow Accordion component independent on core implementation.
 * Properties are inherited from HTML Div element
 */
export interface AccordionProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /**
   * The content of the component.
   */
  items: Array<AccordionItem>;
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: object;
  /**
   * Callback fired when the expand/collapse state is changed.
   */
  onChange?: (panel: string, value: boolean) => void;
  /**
   *  The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps<Theme>;
  /**
   * If true, the preview text is showed. If the item.content is no typeof string, no preview will be show.
   */
  previewContent?: boolean;
  /**
   * The component used for the transition.
   */
  TransitionComponent?: JSXElementConstructor<
    TransitionProps & {
      children?: ReactElement;
    }
  >;
  /**
   * Props applied to the transition element.
   */
  TransitionProps?: object;
  /**
   * If true, the transition is disabled.
   */
  disableAnimation?: boolean;
  /**
   * The props provided to the top level container element.
   */
  accordionContainerProps?: object;
  /**
   * If provided, a max height will be given to every accordion panel. The string provided should be in pixels, eg. {number}px . No max height by default.
   */
  accordionPanelMaxHeight?: string;
  /**
   * If true, more than 1 accordion panels can be opened simultaneously.
   */
  allowMultipleExpand?: boolean;
}

function handleMaxChars(text: string, maxChar: number) {
  return text.length > maxChar ? `${text.substring(0, maxChar)}...` : text;
}

export const Accordion = forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      items,
      onChange,
      disableAnimation,
      accordionContainerProps,
      accordionPanelMaxHeight,
      allowMultipleExpand,
      ...props
    },
    ref,
  ) => {
    const [expand, setExpanded] = useState<string | false | object>(false);
    const controlRef = useRef(null);

    useEffect(() => {
      if (!allowMultipleExpand) {
        let id: number | undefined;
        const firstDefaultExpandedItem = items.find((item, index) => {
          if (item.isDefaultExpanded) {
            id = index;
            return item;
          }
          return undefined;
        });

        if (firstDefaultExpandedItem) {
          setExpanded(firstDefaultExpandedItem.id || `panel${id}`);
        }
      } else {
        const expandItems: { [key: string]: boolean } = {};
        for (let index = 0; index < items.length; index++) {
          const itemId = items[index].id || `panel${index}`;

          expandItems[itemId] = !!items[index]?.isDefaultExpanded;
        }

        setExpanded(() => expandItems);
      }
    }, [allowMultipleExpand, JSON.stringify(items)]);

    useEffect(() => {
      if (controlRef && controlRef.current) {
        const controlBtn = (controlRef.current as HTMLElement).querySelector('button');
        if (controlBtn) {
          controlBtn.addEventListener('focusin', (e: FocusEvent) => {
            e.preventDefault();
          });
        }
      }
    }, [controlRef]);

    const handleChange = useCallback(
      (panel: string) => (_: SyntheticEvent, isExpanded: boolean) => {
        onChange && onChange(panel, isExpanded);

        if (!allowMultipleExpand) {
          setExpanded(isExpanded ? panel : false);
        } else {
          setExpanded((curr: object) => {
            return { ...curr, [panel]: isExpanded };
          });
        }
      },
      [allowMultipleExpand, onChange],
    );

    const handlePreviewContent = useCallback((content: string | ReactNode): string | ReactNode => {
      if (typeof content === 'string') return handleMaxChars(content, 100);

      return content;
    }, []);

    const getTransitionProps = useMemo(() => {
      if (disableAnimation) return { timeout: 0 };
      else return props.TransitionProps;
    }, [disableAnimation, props.TransitionProps]);

    return (
      <Box ref={ref} {...accordionContainerProps} data-testid="Accordion">
        {items.map((item, index) => {
          const panel = item.id || `panel${index}`;
          const isExpanded = allowMultipleExpand
            ? !!(expand as { [key: string]: boolean | undefined })[panel]
            : expand === panel;
          const isContentTypeString = typeof item.content === 'string';

          return (
            <AccordionStyled
              {...props}
              id={panel}
              key={panel}
              className={panel}
              TransitionProps={getTransitionProps}
              expanded={isExpanded}
              onChange={handleChange(panel)}
              isContentTypeString={isContentTypeString}
              disableGutters
              accordionPanelMaxHeight={accordionPanelMaxHeight}
              square
              data-testid="Accordion.item"
            >
              <AccordionSummary
                className="subheadline"
                aria-controls={`${panel}d-content`}
                id={`${panel}d-header`}
                expandIcon={
                  <IconButton
                    ref={controlRef}
                    data-testid="Accordion.btn"
                    sx={{ color: (theme) => theme.palette.text.primary }}
                  >
                    <Icon color="inherit" name="chevron-down" />
                  </IconButton>
                }
                data-testid="Accordion.summary"
              >
                <div className="accordion-panel-title">
                  {item.title} {item.titleValue !== '' ? <LabelStyled>{item.titleValue}</LabelStyled> : ''}
                </div>

                {props.previewContent && isContentTypeString && !isExpanded && (
                  <div className="accordion-panel-title--sneak-peak" data-testid="Accordion.peek">
                    {handlePreviewContent(item.content)}
                  </div>
                )}
              </AccordionSummary>
              <AccordionDetails data-testid="Accordion.content">
                <div className="text">{item.content}</div>
              </AccordionDetails>
            </AccordionStyled>
          );
        })}
      </Box>
    );
  },
);

const AccordionStyled = styled(MuiAccordion, {
  shouldForwardProp: (prop) =>
    prop !== 'previewContent' &&
    prop !== 'expandIcon' &&
    prop !== 'isContentTypeString' &&
    prop !== 'accordionPanelMaxHeight',
})<
  MuiAccordionProps & {
    previewContent?: boolean;
    isContentTypeString?: boolean;
    accordionPanelMaxHeight?: string;
  }
>(({ theme, ...args }) => {
  let style = {};

  if (args.accordionPanelMaxHeight) {
    style = {
      ...style,
      '& .text': {
        maxHeight: args.accordionPanelMaxHeight,
        overflowY: 'auto',
      },
    };
  }

  return style;
});

/**
 * @description Label styling definition
 */

const LabelStyled = styled('span')(({ theme }) => ({
  color: theme.palette.text.secondary,
  fonSize: '11px',
  lineHeight: '16px',
  letterSpacing: '3px',
  fontStyle: 'italic',
  fontWeight: '900',
  margin: '0',
  whiteSpace: 'nowrap',
  textRendering: 'geometricPrecision',
}));
